/**
 * TransferDrawer — right-side slide-over for call transfer.
 *
 * WHY A DRAWER AND NOT AN INLINE PANEL
 * ------------------------------------
 * The softphone is a 300px sidebar — an inline transfer list crammed into it
 * pushed the keypad and End button off screen. The old portal used a
 * right-edge Panel for exactly this. It also cannot live INSIDE the
 * softphone component — the phone occupies a fixed 300px right drawer,
 * so this mounts at AgentLayout level next to CallWorkDrawer.
 *
 * STRUCTURE (mirrors the legacy Panel):
 *   • "Phone" free-text destination at top — an uncurated number must
 *     always be reachable.
 *   • One labelled <select> per destination family: Direct Transfers,
 *     Users, IVR, CSAT IVR, Call Center, Queue, Ring Group, Play N Go.
 *   • Footer: BLIND TRANSFER + ATTENDED TRANSFER.
 *
 * TARGET SYNTAX consumed by POST /agent/calls/:callId/transfer:
 *   `dest:<module>:<id>` — a routing destination handed to the worker's
 *     destination dispatcher (queue → ACD, IVR → menu, ring group →
 *     members, play-n-go → prompt + onward).
 *   plain digits/extension — a dialable endpoint: blind bridges
 *     caller↔target; attended conferences the answered leg into the
 *     call's existing bridge (3-way) — hanging up completes it.
 *
 * Attended needs something to DIAL — a queue or IVR has no endpoint, so
 * Attended disables whenever the selection is a `dest:` target.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { X, PhoneForwarded, Users } from 'lucide-react'
import { apiGet, apiPost } from '../../api/client.js'
import { useTransferDestinations, type TransferDestinationRow } from '../../hooks/useAgentDirectory.js'
import Select from '../ui/Select.js'

export interface TransferDrawerProps {
  open: boolean
  /** Live call id — required for the server-side transfer action. */
  callId?: string | null
  remoteNumber?: string | null
  onResume?: () => Promise<void>
  /** Client-side REFER fallback — only used when no callId exists. */
  onTransfer: (target: string) => void
  /** Ends the agent's own leg — fired ~1.5s after a BLIND transfer so the
   *  worker's caller↔target handoff finishes first. */
  onHangup: () => void
  onClose: () => void
}

interface Selection {
  label: string
  blindTarget: string
  /** Present only for dialable rows — conference needs an endpoint. */
  attendTarget?: string | undefined
}

interface DestSelect {
  label: string
  placeholder: string
  rows: TransferDestinationRow[]
  blindTarget: (r: TransferDestinationRow) => string
  attendTarget?: (r: TransferDestinationRow) => string
}

export function TransferDrawer({ open, callId, remoteNumber, onResume, onHangup, onClose }: TransferDrawerProps): JSX.Element | null {
  const dests = useTransferDestinations()
  const [phone, setPhone] = useState('')
  const [sel, setSel] = useState<Selection | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)
  useEffect(() => {
    generation.current += 1
    return () => { generation.current += 1 }
  }, [open, remoteNumber])

  // Reset on open — a stale selection from the previous call must not
  // transfer THIS caller somewhere unexpected.
  useEffect(() => {
    if (open) { setPhone(''); setSel(null); setError(null); setBusy(false) }
  }, [open, remoteNumber])

  const groups = dests.groups
  const sections: DestSelect[] = [
    {
      label: 'Direct Transfers',
      placeholder: 'Select a Transfer',
      rows: groups?.directTransfers ?? [],
      blindTarget: (r) => r.number ?? '',
      attendTarget: (r) => r.number ?? '',
    },
    {
      label: 'Users',
      placeholder: 'Select User',
      rows: groups?.users ?? [],
      // Blind routes via the dispatcher's dialUser (hot-desk aware);
      // attended originates the extension into the call's bridge.
      blindTarget: (r) => `dest:users:${r.id}`,
      attendTarget: (r) => r.number ?? '',
    },
    { label: 'IVR', placeholder: 'Select IVR', rows: groups?.ivr ?? [], blindTarget: (r) => `dest:ivr:${r.id}` },
    { label: 'CSAT IVR', placeholder: 'Select CSAT IVR', rows: groups?.csatIvr ?? [], blindTarget: (r) => `dest:ivr:${r.id}` },
    {
      label: 'Call Center',
      placeholder: 'Select Callcenter',
      rows: groups?.callCenters ?? [],
      blindTarget: (r) => `dest:contact_center_queues:${r.id}`,
    },
    {
      label: 'Queue',
      placeholder: 'Select Queue',
      rows: groups?.queues ?? [],
      // Rows keep their source table — `queues` and `fifo_queues` hit
      // different dispatcher branches.
      blindTarget: (r) => `dest:${r.table ?? 'queues'}:${r.id}`,
    },
    { label: 'Ring Group', placeholder: 'Select RingGroup', rows: groups?.ringGroups ?? [], blindTarget: (r) => `dest:ring_groups:${r.id}` },
    { label: 'Play N Go', placeholder: 'Select Play & Go', rows: groups?.playNGo ?? [], blindTarget: (r) => `dest:play_n_go:${r.id}` },
  ]

  // The effective target: a typed phone number wins over the selects; the
  // selects are mutually exclusive (choosing one clears the others, like the
  // legacy comboboxes which all wrote the same txphone).
  const target: Selection | null = phone.trim()
    ? { label: phone.trim(), blindTarget: phone.trim(), attendTarget: phone.trim() }
    : sel

  async function transfer(mode: 'blind' | 'attended'): Promise<void> {
    if (!target || !target.blindTarget || busy || (mode === 'attended' && !target.attendTarget)) return
    const t = mode === 'attended' ? target.attendTarget! : target.blindTarget
    const currentGeneration = generation.current
    setBusy(true)
    setError(null)
    try {
      if (mode === 'attended') await onResume?.()
      if (generation.current !== currentGeneration) return
      const res = await apiPost<{ accepted?: boolean; callId: string; requestId: string }>(
        `/agent/calls/${callId || 'current'}/transfer`,
        { target: t, mode, ...(remoteNumber ? { remoteNumber } : {}) },
      )
      if (!res.accepted || !res.callId || !res.requestId) throw new Error('Transfer was not accepted by the server.')
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (generation.current !== currentGeneration) return
        const result = await apiGet<{ outcome: string; details?: string }>(
          `/agent/calls/${res.callId}/transfer-status?requestId=${encodeURIComponent(res.requestId)}`,
        )
        if (generation.current !== currentGeneration) return
        if (result.outcome === 'executed') {
          // The agent leg must outlive the worker's handoff — a short delay
          // then BYE.
          if (mode === 'blind') onHangup()
          onClose()
          return
        }
        if (result.outcome !== 'pending') throw new Error(result.details || 'Transfer failed. Your original call is still connected.')
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      throw new Error('Transfer confirmation timed out. Check the current call before trying again.')
    } catch (err) {
      if (generation.current === currentGeneration) setError(err instanceof Error ? err.message : 'Transfer failed')
    } finally {
      if (generation.current === currentGeneration) setBusy(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[65] bg-black/30 backdrop-blur-[1px]" onClick={() => { if (!busy) onClose() }} />
      <aside className="fixed inset-y-0 right-0 z-[70] flex w-[400px] flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/60 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-blue-50">
              <PhoneForwarded className="h-4 w-4 text-[#3366FF]" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">Call Transfer</div>
              <div className="text-[10px] text-slate-500">Move this caller to a number or destination</div>
            </div>
          </div>
          <button onClick={() => { if (!busy) onClose() }} className="rounded-[3px] p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Phone — free-typed destination */}
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Phone
            </label>
            <input
              type="text"
              value={phone}
              disabled={busy}
              onChange={(e) => { setPhone(e.target.value); setSel(null); setError(null) }}
              placeholder="Destination Number"
              className="w-full rounded-[3px] border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-blue-400"
            />
          </div>

          {dests.loading && <p className="text-[11px] text-slate-400">Loading destinations…</p>}
          {dests.error && (
            <p className="rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
              Destination list unavailable ({dests.error}) — use a number above.
            </p>
          )}

          {sections.map((sec) => {
            const usable = sec.rows.filter((r) => {
              const s = r.status
              // Defensive layer — the API already excludes the current user
              // and offline/busy agents. Mirror the busy filter here so a stale
              // cached response cannot offer a transfer to an agent already on
              // a call.
              return s !== 'OFFLINE' && s !== 'LOGGED_OUT'
                && s !== 'IN_CALL' && s !== 'RINGING'
                && s !== 'ON_CALL' && s !== 'WRAP_UP'
                && s !== 'ACW'
            })
            const value = sel && usable.some((r) => sec.blindTarget(r) === sel.blindTarget) ? sel.blindTarget : ''
            return (
              <div key={sec.label}>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {sec.label}
                </label>
                <Select
                  ariaLabel={sec.label}
                  value={value}
                  disabled={busy || usable.length === 0}
                  onChange={(v) => {
                    setPhone('')
                    const row = usable.find((r) => sec.blindTarget(r) === v)
                    setSel(row ? { label: row.name, blindTarget: sec.blindTarget(row), attendTarget: sec.attendTarget?.(row) } : null)
                    setError(null)
                  }}
                >
                  <option value="">{usable.length === 0 ? `No ${sec.label.toLowerCase()} configured` : sec.placeholder}</option>
                  {usable.map((r) => (
                    <option key={r.id} value={sec.blindTarget(r)}>
                      {r.name}{r.number ? ` · ${r.number}` : ''}{r.status ? ` · ${r.status}` : ''}
                    </option>
                  ))}
                </Select>
              </div>
            )
          })}

          {!dests.loading && !dests.error && sections.every((s) => s.rows.length === 0) && (
            <p className="text-[11px] text-slate-400">
              No transfer destinations configured — ask an admin to add queues, IVRs or colleagues.
            </p>
          )}

          {busy && <p role="status" className="text-xs text-blue-700">Connecting transfer destination. Keep this call open until the handoff is confirmed.</p>}
          {error && (
            <p role="alert" className="rounded-[3px] border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">{error}</p>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            onClick={() => void transfer('blind')}
            disabled={!target || busy}
            title="Blind transfer — hands the caller over immediately"
            className="flex flex-1 items-center justify-center gap-2 rounded-[3px] bg-blue-600 px-3 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
          >
            <PhoneForwarded className="h-4 w-4" />
            Blind Transfer
          </button>
          <button
            onClick={() => void transfer('attended')}
            disabled={!target?.attendTarget || busy}
            title={
              target && !target.attendTarget
                ? 'Attended needs a dialable target — pick a user, a number, or type one'
                : 'Attended transfer — 3-way conference; hang up to leave them connected'
            }
            className="flex flex-1 items-center justify-center gap-2 rounded-[3px] bg-emerald-600 px-3 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
          >
            <Users className="h-4 w-4" />
            Attended Transfer
          </button>
        </div>
      </aside>
    </>
  )
}
